import React, { useState, useEffect } from "react";
import { Box, Stack, Typography, Button, Chip } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { WriterInfo } from "../../components/WriterInfo/WriterInfo";
import { IconThreads } from "../../assets/icon/metric/IconThreads";
import {
	extractDominantColor,
	getContrastingTextColors,
	createGradientBackground,
} from "../../utils/colorExtraction";
import { useCachedAvatar } from "../../hooks/useCachedAvatar";

/**
 * ParticipantShowcaseCard - Premium participant card with dynamic theming
 * @param {object} props
 * @param {object} props.user - User data object [Required]
 * @param {boolean} props.isMobile - Mobile responsive flag [Required]
 * @param {string|null} props.fallbackAvatarUrl - Fallback avatar URL [Optional]
 * @param {string} props.bio - 한줄 소개 [Optional]
 * @param {string|null} props.businessLink - 비지니스 링크(https) [Optional]
 * @param {string[]} props.categoryLabels - 카테고리 라벨 목록 [Optional]
 */
const ParticipantShowcaseCard = ({
	user,
	isMobile,
	fallbackAvatarUrl = null,
	bio = "",
	businessLink = null,
  categoryLabels = [],
}) => {
	const theme = useTheme();
  const isXs360 = useMediaQuery('(max-width:360px)');
	const username = user?.profile?.username || user?.profile?.name || "";
	const displayName = user?.profile?.name || "";
	const userId = String(user?.id || user?.user_id || user?.profile?.id || "");

	// Avatar loading with cache
	const { imageSrc } = useCachedAvatar(userId, fallbackAvatarUrl, 320);

	// Hover 상태 및 동적 테마 상태
	const [isHovered, setIsHovered] = useState(false);

	// State for dynamic theming
	const [cardTheme, setCardTheme] = useState({
		backgroundColor: theme.palette.background.paper,
		textColors: getContrastingTextColors(false),
		isDark: false,
		isLoading: true,
	});

	// Extract color when image loads
	useEffect(() => {
		if (!imageSrc) {
			setCardTheme((prev) => ({ ...prev, isLoading: true }));
			return;
		}

		const extractColor = async () => {
			try {
				setCardTheme((prev) => ({ ...prev, isLoading: true }));

				// Extract dominant color from avatar
				const colorData = await extractDominantColor(imageSrc);
				const textColors = getContrastingTextColors(colorData.isDark);
				const gradient = createGradientBackground(
					colorData.color,
					colorData.isDark
				);

				// Validate extracted colors before setting
				const isValidHex = /^#([A-Fa-f0-9]{6})$/.test(colorData.color);
				const isValidGradient =
					gradient &&
					typeof gradient === "string" &&
					gradient.includes("linear-gradient");

				setCardTheme({
					backgroundColor: isValidHex
						? colorData.color
						: theme.palette.background.paper,
					backgroundGradient: isValidGradient ? gradient : colorData.color,
					textColors: textColors,
					isDark: colorData.isDark,
					isLoading: false,
				});
			} catch (error) {
				console.error("Failed to extract color:", error);
				setCardTheme({
					backgroundColor: theme.palette.background.paper,
					textColors: getContrastingTextColors(false),
					isDark: false,
					isLoading: false,
				});
			}
		};

		extractColor();
	}, [imageSrc, theme]);

	const handleThreadsClick = () => {
		const threadsUrl = `https://www.threads.net/@${username}`;
		window.open(threadsUrl, "_blank", "noopener,noreferrer");
	};

	const cardHeight = isMobile ? 'auto' : 320;

	return (
		<Box
			sx={{
				width: "100%",
				height: cardHeight,
				borderRadius: theme.spacing(1),
				overflow: "hidden",
				position: "relative",
				border: `1px solid ${theme.palette.divider}`,
			}}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
		>
			{/* Background with gradient */}
			<Box
				sx={{
					position: "absolute",
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					background: cardTheme.backgroundGradient || cardTheme.backgroundColor,
					opacity: isHovered && !cardTheme.isLoading ? 1 : 0,
					transition: "opacity 0.35s ease, background 0.2s ease",
					pointerEvents: 'none',
				}}
			/>

			{/* Content */}
			<Stack
				sx={{
					position: "relative",
					height: "100%",
					p: isMobile ? 2 : 3,
					justifyContent: "space-between",
					gap: 5, // slightly reduced
				}}
			>
				{/* Top Section - User Info using WriterInfo */}
				<Box sx={{ maxWidth: "100%", overflow: "hidden" }}>
					<WriterInfo
						profileImage={
							fallbackAvatarUrl || user?.profile?.threads_profile_picture_url
						}
						userID={userId}
						username={username}
						name={displayName}
						size="medium"
						useWhiteText={isHovered && cardTheme.isDark}
						sx={{
							backgroundColor: "transparent",
							padding: 0,
						}}
					/>
					{(() => {
						const labels = Array.isArray(categoryLabels) && categoryLabels.length > 0
							? categoryLabels
							: (Array.isArray(user?.categoryLabels) && user.categoryLabels.length > 0
								? user.categoryLabels
								: (Array.isArray(user?.__categoryLabels) ? user.__categoryLabels : []));
							return labels && labels.length > 0 ? (
							<Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
								{labels.slice(0, 4).map((label, idx) => (
									<Chip
										key={`${label}-${idx}`}
										label={label}
										size="small"
										variant="outlined"
										sx={{
												color: theme.palette.primary.main,
												borderColor: theme.palette.primary.main,
												transition: 'color 0.3s ease, border-color 0.3s ease, background-color 0.3s ease',
												"& .MuiChip-label": { px: 0.5, fontSize: "0.75rem" },
											}}
									/>
								))}
							</Box>
						) : null;
					})()}
					<Typography
						variant="body2"
							sx={{
							mt: 3,
								color: theme.palette.primary.main,
							display: "-webkit-box",
              wordBreak: "keep-all",
							WebkitLineClamp: 2,
							WebkitBoxOrient: "vertical",
							overflow: "hidden",
							textOverflow: "ellipsis",
							lineHeight: 1.5,
							minHeight: "3em",
							fontSize: "0.875rem",
								transition: 'color 0.3s ease',
						}}
					>
						{bio || "\u00a0"}
					</Typography>
				</Box>

				{/* Bottom Section - Threads Buttons (stacked vertically) */}
				<Box
					sx={{
						display: "flex",
						flexDirection: isMobile || isXs360 ? "column" : "row",
						justifyContent: isMobile || isXs360 ? "flex-start" : "space-between",
						alignItems: isMobile || isXs360 ? "stretch" : "center",
						overflow: 'hidden',
					}}
				>
					<Stack
						direction="column"
						spacing={1}
						sx={{ alignItems: "flex-start", width: "100%", minWidth: 0 }}
					>
						<Button
							variant="outlined"
							startIcon={<IconThreads size={18} />}
							onClick={handleThreadsClick}
							disabled={cardTheme.isLoading}
							sx={{
								width: "100%",
								backgroundColor: "transparent",
								color: theme.palette.primary.main,
								textTransform: "none",
								fontWeight: 600,
								fontSize: "0.875rem",
								px: 2,
								py: 0.75,
								borderRadius: theme.spacing(2.5),
								border: `1px solid ${theme.palette.primary.main}`,
								"&:hover": {
									backgroundColor: "transparent",
									border: `1px solid ${theme.palette.primary.main}`,
								},
								"&.Mui-disabled": {
									opacity: 0.5,
								},
								transition: 'color 0.3s ease, border-color 0.3s ease, background-color 0.3s ease',
							}}
						>
							Threads
						</Button>
						{!!businessLink && (
							<Button
								variant="outlined"
								onClick={() =>
									window.open(businessLink, "_blank", "noopener,noreferrer")
								}
								disabled={cardTheme.isLoading}
							sx={{
									backgroundColor: "transparent",
								color: theme.palette.primary.main,
									textTransform: "none",
									width: "100%",
									fontWeight: 600,
									fontSize: "0.875rem",
									px: 2,
									py: 0.75,
									borderRadius: theme.spacing(2.5),
								border: `1px solid ${theme.palette.primary.main}`,
									"&:hover": {
										backgroundColor: "transparent",
									border: `1px solid ${theme.palette.primary.main}`,
									},
									"&.Mui-disabled": {
										opacity: 0.5,
									},
								transition: 'color 0.3s ease, border-color 0.3s ease, background-color 0.3s ease',
								}}
							>
								비지니스 링크
							</Button>
						)}
					</Stack>

					{/* Optional: Add follower count or other metrics */}
					{user?.profile?.follower_count && !cardTheme.isLoading && (
						<Typography
							variant="caption"
							sx={{
								color: cardTheme.textColors.secondary,
								fontWeight: 500,
								mt: isMobile || isXs360 ? 1 : 0,
								alignSelf: isMobile || isXs360 ? 'flex-end' : 'auto',
								maxWidth: '100%',
								overflow: 'hidden',
								textOverflow: 'ellipsis',
								whiteSpace: 'nowrap',
							}}
						>
							{new Intl.NumberFormat("ko-KR").format(
								user.profile.follower_count
							)}{" "}
							팔로워
						</Typography>
					)}
				</Box>
			</Stack>
		</Box>
	);
};

export default ParticipantShowcaseCard;
